/**
 * Unified customer-artwork upload helper.
 *
 * Picks the right transport per file:
 *   • Files <= 40 MB → direct multipart POST to /artwork-upload (single
 *     round-trip, fast for small JPEGs/PDFs).
 *   • Files >  40 MB → chunked S3 multipart via the upload-session
 *     pipeline, then a JSON "register" call to link the resulting
 *     order_files into the repro_job. This bypasses the Kubernetes
 *     ingress body-size limit (~50 MB) and the FastAPI single-shot
 *     `await file.read()` memory cliff that broke 282 MB uploads in
 *     production.
 *
 * Returns a promise that resolves once every selected file is uploaded
 * and registered. Failure of any one file rejects with a descriptive
 * Error so the caller can render the actual server response instead of
 * a generic "Failed to upload artwork".
 *
 * onProgress is called with { phase, fileIndex, totalFiles,
 *   filename, percent, completed } so the caller can render a real
 * progress bar.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// 40 MB — below the typical 50 MB ingress body limit so we leave
// headroom for multipart overhead.
const DIRECT_UPLOAD_LIMIT_BYTES = 40 * 1024 * 1024;
// Chunked transport uses 8 MB parts which is well above S3's 5 MB
// multipart minimum and gives a good throughput/parallelism trade.
const CHUNK_SIZE_BYTES = 8 * 1024 * 1024;

function getToken() {
    return localStorage.getItem('token');
}

function authHeaders() {
    return { Authorization: `Bearer ${getToken()}` };
}

/**
 * Format a server-side error into the most useful human-readable
 * message we can. Falls through HTTP status → response.data.detail →
 * raw response body → the Error's own message. This is the function
 * the UI should display instead of swallowing into "Failed to upload".
 */
export function describeUploadError(err) {
    if (!err) return 'Unknown upload error';
    if (err?.response) {
        const status = err.response.status;
        const data = err.response.data;
        if (data?.detail) {
            return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        }
        if (typeof data === 'string' && data.length < 400) {
            return data;
        }
        if (status === 413) {
            return 'File too large for the server proxy. Upload was rejected by the network layer before reaching the backend.';
        }
        if (status === 504 || status === 408) {
            return `Upload timed out (HTTP ${status}). Try again with a stable network or split the upload.`;
        }
        return `HTTP ${status}${err.response.statusText ? ' — ' + err.response.statusText : ''}`;
    }
    if (err?.message) return err.message;
    return 'Upload failed';
}

async function uploadDirect(orderId, files, onProgress) {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const res = await axios.post(
        `${API}/orders/${orderId}/artwork-upload`,
        formData,
        {
            headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => {
                if (!onProgress) return;
                const percent = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
                onProgress({
                    phase: 'direct',
                    fileIndex: 0,
                    totalFiles: files.length,
                    filename: files.length === 1 ? files[0].name : `${files.length} files`,
                    percent,
                    completed: percent === 100,
                });
            },
        },
    );
    return res.data;
}

async function uploadFileChunked(orderId, file, fileIndex, totalFiles, onProgress) {
    const initRes = await axios.post(
        `${API}/files/upload/chunked/init`,
        {
            order_id: orderId,
            filename: file.name,
            file_size: file.size,
            content_type: file.type || 'application/octet-stream',
            category: 'artwork',
            chunk_size: CHUNK_SIZE_BYTES,
        },
        { headers: authHeaders() },
    );
    const { upload_session_id, file_id, chunk_size, total_chunks } = initRes.data;
    const effectiveChunkSize = chunk_size || CHUNK_SIZE_BYTES;
    const totalChunks = total_chunks || Math.max(1, Math.ceil(file.size / effectiveChunkSize));

    for (let i = 0; i < totalChunks; i++) {
        const start = i * effectiveChunkSize;
        const end = Math.min(start + effectiveChunkSize, file.size);
        const chunk = file.slice(start, end);
        const partNumber = i + 1;

        // 1. Get a presigned URL for this part.
        const presignRes = await axios.post(
            `${API}/files/upload/chunked/presign`,
            { upload_session_id, part_number: partNumber },
            { headers: authHeaders() },
        );
        const { upload_url } = presignRes.data;

        // 2. PUT the chunk straight to S3. We use native fetch (NOT axios)
        //    because axios.defaults.headers.common['Authorization'] is set
        //    globally for backend calls — sending that Bearer token to S3
        //    triggers AuthorizationHeaderMalformed / 400 since sigv4 expects
        //    the signature in X-Amz-Signature query param only, with no
        //    Authorization header. fetch() does not inherit axios defaults.
        const partRes = await fetch(upload_url, {
            method: 'PUT',
            body: chunk,
            headers: { 'Content-Type': 'application/octet-stream' },
        });
        if (!partRes.ok) {
            const errBody = await partRes.text().catch(() => '');
            throw new Error(`S3 upload failed for part ${partNumber}: HTTP ${partRes.status} ${partRes.statusText}${errBody ? ' — ' + errBody.slice(0, 300) : ''}`);
        }
        const etag = (partRes.headers.get('etag') || partRes.headers.get('ETag') || '').replace(/"/g, '');

        // 3. Tell the backend the part landed.
        await axios.post(
            `${API}/files/upload/chunked/part-uploaded`,
            {
                upload_session_id,
                part_number: partNumber,
                etag,
                size: chunk.size,
            },
            { headers: authHeaders() },
        );

        if (onProgress) {
            const percent = Math.round(((i + 1) / totalChunks) * 100);
            onProgress({
                phase: 'chunked',
                fileIndex,
                totalFiles,
                filename: file.name,
                percent,
                completed: i + 1 === totalChunks,
            });
        }
    }

    await axios.post(
        `${API}/files/upload/chunked/complete`,
        { upload_session_id },
        { headers: authHeaders() },
    );
    return file_id;
}

export async function uploadCustomerArtwork(orderId, files, onProgress) {
    if (!files || files.length === 0) {
        throw new Error('No files selected');
    }

    const smallFiles = files.filter(f => f.size <= DIRECT_UPLOAD_LIMIT_BYTES);
    const largeFiles = files.filter(f => f.size > DIRECT_UPLOAD_LIMIT_BYTES);

    let result = { job_id: null, job_created: false, files: [] };

    if (smallFiles.length > 0) {
        const directRes = await uploadDirect(orderId, smallFiles, onProgress);
        result = directRes;
    }

    if (largeFiles.length > 0) {
        const fileIds = [];
        for (let i = 0; i < largeFiles.length; i++) {
            const fid = await uploadFileChunked(
                orderId, largeFiles[i], i, largeFiles.length, onProgress,
            );
            fileIds.push(fid);
        }
        const registerRes = await axios.post(
            `${API}/orders/${orderId}/artwork-upload/register`,
            { file_ids: fileIds },
            { headers: authHeaders() },
        );
        // Merge with any small-file result so the caller sees the union.
        result = {
            ...registerRes.data,
            job_created: result.job_created || registerRes.data.job_created,
            files: [...(result.files || []), ...(registerRes.data.files || [])],
        };
    }

    return result;
}
